import { Client } from '@1po/1po-bff-fe-spec/generated/my_network/response/MyNetworkClientsResponse';
import { ClientSettingsFormType } from './GarageDetails.types';

/**
 * Normalises a BigDecimal string from the API to a canonical JS decimal string,
 * stripping trailing zeros so the result matches the format produced by InputPrice
 * (number.toString()). Returns undefined for missing, empty, or zero values.
 *
 * @example
 * toCanonicalAmountString('1000.00') // '1000'
 * toCanonicalAmountString('1299.99') // '1299.99'
 * toCanonicalAmountString('0')       // undefined
 * toCanonicalAmountString(undefined) // undefined
 */
export const toCanonicalAmountString = (value: string | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }
  const num = Number.parseFloat(value);
  return Number.isNaN(num) || num === 0 ? undefined : String(num);
};

/**
 * Maps Client data to form initial values
 *
 * @param client - Client data from API (or partial for testing)
 * @returns Form values for ClientSettingsFormType
 */
export const mapClientToFormValues = (client: Partial<Client>): ClientSettingsFormType => ({
  deliveryMethod: client.deliveryMethod ?? undefined,
  automaticOrderTransfer: !!client.automaticOrderTransfer,
  manageCreditLimit: !!client.manageCreditLimit,
  maxOrderAmount: toCanonicalAmountString(client.maxOrderAmount),
  maxInvoiceAmount: toCanonicalAmountString(client.maxInvoiceAmount),
  active: !!client.active,
});

/**
 * Checks if form values have changed from initial client data
 *
 * @param client - Original client data (or partial for testing)
 * @param formValues - Current form values
 * @returns True if any value has changed
 */
export const haveFormValuesChanged = (
  client: Partial<Client>,
  formValues: Partial<ClientSettingsFormType>,
): boolean => {
  return Object.entries(formValues).some(([field, value]) => {
    const clientValue = client[field as keyof Client];
    return value !== undefined && clientValue !== value;
  });
};

/**
 * Converts string value to number or undefined
 * Handles empty strings, null, and invalid numbers (NaN)
 * Returns undefined for zero values
 *
 * @param value - Value to convert (string, number, null, or undefined)
 * @returns Converted number or undefined
 *
 * @example
 * toNumberOrUndefined('123')    // 123
 * toNumberOrUndefined('')       // undefined
 * toNumberOrUndefined(null)     // undefined
 * toNumberOrUndefined('0')      // undefined
 * toNumberOrUndefined('abc')    // undefined (NaN)
 */
export const toNumberOrUndefined = (value: string | number | null | undefined): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const num = Number(value);
  return Number.isNaN(num) || num === 0 ? undefined : num;
};
